// Tagged `customers` by the generator, not `reporting` — orval's tags follow the backend controller.
import { apiErrorMessage } from '@agos/api-client'
import { useGetAnalyticsRevenue } from '@agos/api-client/customers'
import { useGetFiskalRegisters } from '@agos/api-client/fiskal'
import {
  getGetSalesChannelsQueryKey,
  useDeleteSalesChannelsId,
  useGetSalesChannels,
  usePatchSalesChannelsId,
  usePostSalesChannels,
} from '@agos/api-client/orders'
import {
  CATEGORY_PALETTE,
  SHIPPED_REGISTER_NAMES,
  T,
  formatCount,
  registerName,
  toDateParam,
  tt,
  useAuth,
  useModalFocus,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  PageHeader,
  Spinner,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Plus, Store, Trash2, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  CAPABILITIES,
  canSave,
  capabilityApplies,
  capabilityLabel,
  capabilityMeaning,
  capabilityOf,
  channelPayload,
  choosesItsRegister,
  printRegisterChoices,
  togglePrintRegister,
  channelsOf,
  deactivateWarning,
  deleteBlocked,
  deleteWarning,
  emptyForm,
  formOf,
  isBlocking,
  ordersFor,
  problemsOf,
  registerChoices,
  reachNote,
  tradeOf,
  type Channel,
  type ChannelForm,
  type ChannelProblem,
} from './sales-channels'

/**
 * `/sales-channels` — §9.5's channel administration.
 *
 * **A justified EXTRA in the parity diff**: legacy has no such screen, and `route-diff.test.ts` requires
 * every extra to cite the upgrade it implements. This one cites §9.5.
 *
 * §10A.1 gave the backend a channels table whose capabilities decide whether an order can be closed at all,
 * and until now nothing in the frontend could edit them — a franchise adding a channel did it in SQL. The
 * screen's real work is not the seven checkboxes; it is saying **what each one does to an order**, taken
 * from the backend rather than from the flag's name.
 *
 * See `sales-channels.ts` for FB-181 and for where each explanation comes from.
 */

/** The last thirty days, for the *how much does this channel actually carry* figure. */
function lastThirtyDays(): { from: string; to: string } {
  const today = new Date()
  const from = new Date(today)
  from.setDate(from.getDate() - 29)
  return { from: toDateParam(from), to: toDateParam(today) }
}

export function SalesChannelsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuth()
  const writable = mayWrite('sales-channels', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('sales-channels', user?.systemRole, hasPermission)

  const [window] = useState(lastThirtyDays)
  const { data, isLoading } = useGetSalesChannels()
  const revenue = useGetAnalyticsRevenue(window)

  const channels = useMemo(() => channelsOf(data), [data])
  const trade = useMemo(() => tradeOf(revenue.data), [revenue.data])

  const [editing, setEditing] = useState<{ id: number | null; form: ChannelForm } | null>(null)
  const [deleting, setDeleting] = useState<Channel | null>(null)
  const [deactivating, setDeactivating] = useState<Channel | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSalesChannelsQueryKey() })
  }
  // §9.385 - **one helper, three call sites.** It is handed to two mutations as `onError` and called
  // by hand from a third, and it showed a title and nothing else. React Query gives the error to the
  // handler, so taking it here reaches all three at once.
  const onError = (caught: unknown) =>
    toast({
      variant: 'destructive',
      title: tt('Zmena sa neuložila'),
      description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
    })

  const create = usePostSalesChannels({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refresh()
        toast({ title: tt('Kanál vytvorený') })
      },
      onError,
    },
  })
  const update = usePatchSalesChannelsId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        setDeactivating(null)
        refresh()
        toast({ title: tt('Kanál uložený') })
      },
      onError: (caught) => {
        setDeactivating(null)
        onError(caught)
      },
    },
  })
  const remove = useDeleteSalesChannelsId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        refresh()
        toast({ title: tt('Kanál zmazaný') })
      },
      onError: (caught) => {
        setDeleting(null)
        toast({
          variant: 'destructive',
          title: tt('Kanál sa nedá zmazať'),
          description: apiErrorMessage(
                         caught,
                         tt('Server ho odmietol - pravdepodobne naň už sú objednávky. Vypnite ho.'),
                       ),
        })
      },
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Predajné kanály')}
        description={tt('Čím sa objednávka líši podľa toho, kadiaľ prišla - a čo tie prepínače naozaj robia.')}
        icon={<Store className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-add-channel
            disabled={!writable}
            onClick={() => setEditing({ id: null, form: emptyForm(channels) })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            <T>Pridať kanál</T>
          </button>
        }
      />

      {readOnly !== null && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-read-only>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>{readOnly}</span>
        </p>
      )}

      {/* What each flag does, said once at the top rather than guessed per row. */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-2" data-capability-legend>
        <h2 className="text-sm font-bold">
          <T>Čo jednotlivé vlastnosti robia</T>
        </h2>
        {CAPABILITIES.map((key) => (
          <p key={key} className="text-xs text-muted-foreground" data-capability={key}>
            <strong className="text-foreground">{capabilityLabel(key)}</strong> - {capabilityMeaning(key)}
          </p>
        ))}
      </div>

      <div className="space-y-3" data-channels={channels.length}>
        {channels.map((channel) => {
          const orders = ordersFor(channel.slug, trade)
          return (
            <div
              key={channel.id}
              className={cn(
                'rounded-2xl border p-4 space-y-3',
                channel.isActive ? 'border-border bg-card' : 'border-border bg-muted/30',
              )}
              data-channel={channel.slug}
              data-channel-active={channel.isActive ? 'yes' : 'no'}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm flex items-center gap-2">
                    {channel.name}
                    <span className="font-mono text-[11px] text-muted-foreground">{channel.slug}</span>
                    {!channel.isActive && <StatusBadge label={tt('vypnutý')} color="zinc" />}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5" data-reach>
                    {reachNote(channel)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
                    <T>30 dní</T>
                  </p>
                  <p className="font-bold tabular-nums text-sm" data-orders>
                    {orders === null ? tt('bez objednávok') : `${formatCount(orders)} obj.`}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5" data-capabilities>
                {/* §9.726 - a capability that means nothing on this channel gets no chip: *Bez dokladu sa zruší*
                 * is drawn only on a channel that settles immediately. */}
                {CAPABILITIES.filter((key) => capabilityApplies(key, channel)).map((key) => {
                  const enabled = capabilityOf(channel, key)
                  return (
                    <span
                      key={key}
                      title={capabilityMeaning(key)}
                      data-capability-state={`${key}:${enabled ? 'on' : 'off'}`}
                      className={cn(
                        'text-[11px] px-2 py-0.5 rounded-lg border',
                        enabled
                          ? 'border-primary/40 bg-primary/10 text-foreground'
                          : 'border-border text-muted-foreground/50',
                      )}
                    >
                      {capabilityLabel(key)}
                    </span>
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-edit={channel.slug}
                  disabled={!writable}
                  onClick={() => setEditing({ id: channel.id, form: formOf(channel) })}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold disabled:opacity-50"
                >
                  <T>Upraviť</T>
                </button>
                <button
                  type="button"
                  data-toggle={channel.slug}
                  disabled={!writable || update.isPending}
                  onClick={() => setDeactivating(channel)}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold disabled:opacity-50"
                >
                  {channel.isActive ? <T>Vypnúť</T> : <T>Zapnúť</T>}
                </button>
                <button
                  type="button"
                  data-delete={channel.slug}
                  disabled={!writable}
                  onClick={() => setDeleting(channel)}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold text-red-500 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Trash2 className="w-3 h-3" />
                  <T>Zmazať</T>
                </button>
                {deleteBlocked(channel, trade) && (
                  <span className="text-[11px] text-muted-foreground" data-delete-blocked={channel.slug}>
                    <T>má objednávky - server mazanie odmietne</T>
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {editing !== null && (
        <ChannelDialog
          form={editing.form}
          editingId={editing.id}
          others={channels.filter((channel) => channel.id !== editing.id)}
          saving={create.isPending || update.isPending}
          onChange={(form) => setEditing({ ...editing, form })}
          onClose={() => setEditing(null)}
          onSave={() => {
            const payload = channelPayload(editing.form)
            if (editing.id === null) create.mutate({ data: payload })
            else update.mutate({ id: editing.id, data: payload })
          }}
        />
      )}

      <ConfirmDialog
        open={deactivating !== null}
        onClose={() => setDeactivating(null)}
        onConfirm={() =>
          deactivating !== null &&
          update.mutate({ id: deactivating.id, data: { isActive: !deactivating.isActive } })
        }
        title={
          deactivating === null
            ? ''
            : deactivating.isActive
              ? `${tt('Vypnúť kanál')} ${deactivating.name}?`
              : `${tt('Zapnúť kanál')} ${deactivating.name}?`
        }
        description={deactivating === null ? '' : deactivateWarning(deactivating, trade)}
        confirmLabel={deactivating?.isActive === true ? tt('Vypnúť') : tt('Zapnúť')}
        loading={update.isPending}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting !== null && remove.mutate({ id: deleting.id })}
        title={deleting === null ? '' : `${tt('Zmazať kanál')} ${deleting.name}?`}
        description={deleting === null ? '' : deleteWarning(deleting, trade)}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

function ChannelDialog({
  form,
  editingId,
  others,
  saving,
  onChange,
  onClose,
  onSave,
}: {
  form: ChannelForm
  editingId: number | null
  others: Channel[]
  saving: boolean
  onChange: (form: ChannelForm) => void
  onClose: () => void
  onSave: () => void
}) {
  // §10A.35 — which registers exist is the franchise's business, not a constant. Falls back to the
  // two the port shipped with until the call answers, so the picker is never empty.
  const { data: registerData } = useGetFiskalRegisters()
  const registerRows = Array.isArray(registerData)
    ? (registerData as Array<{ kasaId?: string | null; label?: string | null }>)
    : []
  /* §9.650 - `registerIds` and `registerName` out of `@agos/shared`, which §9.578 extracted for this very
     feature, rather than this screen's own copy of the shipped pair.

     **And the stored id is always offered**, which is not defensive: `/fiskal/registers` is a second request, so
     between the first paint and its answer - or for ever if it fails - a `<select>` whose value matches no
     `<option>` renders **blank** over a configured register, which is §9.562's trap. `registerName` prints the
     id itself when nothing names it, so a reader meets `terasa` rather than an empty box. */
  const registerOptions = registerChoices(form.kasaId, registerData).map((kasaId) => ({
    kasaId,
    label: registerName(kasaId, registerRows, SHIPPED_REGISTER_NAMES),
  }))

  /* §9.740, owner O110 - the registers a channel both on the premises and driven may print on. Drawn in the order
     the register list answers, plus any ticked one it no longer names (`printRegisterChoices` says why). */
  const printRegisterOptions = printRegisterChoices(form.printKasaIds, registerData)

  const problems = problemsOf(form, others)

  /* §9.495, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card (§9.469); the close matches the scrim's own tap,
     which is unconditional here because this overlay holds no write of its own (§9.491). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editingId === null ? tt('Nový kanál') : tt('Upraviť kanál')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-channel-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm">
            {editingId === null ? tt('Nový kanál') : `${tt('Upraviť')} ${form.name || form.slug}`}
          </h3>
          <button type="button" onClick={onClose} aria-label={tt('Zatvoriť')}
 data-dialog-cancel className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={tt('Názov')}>
              <input
                value={form.name}
                onChange={(event) => onChange({ ...form, name: event.target.value })}
                aria-label={tt('Názov')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Kľúč')}>
              <input
                value={form.slug}
                onChange={(event) => onChange({ ...form, slug: event.target.value })}
                aria-label={tt('Kľúč')}
                // The slug is what every backend query looks the channel up by, and what `orders.type`
                // stores. Changing it on a channel with orders orphans them.
                disabled={editingId !== null}
                className={cn(INPUT, 'font-mono')}
              />
            </Field>
            <Field label={tt('Poradie')}>
              <input
                value={form.sortOrder}
                onChange={(event) => onChange({ ...form, sortOrder: event.target.value })}
                aria-label={tt('Poradie')}
                className={INPUT}
              />
            </Field>
          </div>

          {/* §10A.17 - which register this channel's receipts print on. *Podľa typu predaja* is the
            * capability rule the server has always applied, and leaving it there is what every
            * franchise does today; naming a register overrides it. Offered as a choice rather than
            * derived from `onPremise`, because a terrace is on the premises and may still have its
            * own kasa, and two delivery channels cannot print on two registers otherwise. */}
          <Field label={tt('Kasa')}>
            <select
              value={form.kasaId}
              onChange={(event) => onChange({ ...form, kasaId: event.target.value })}
              aria-label={tt('Kasa')}
              data-channel-kasa
              className={INPUT}
            >
              <option value="">{tt('Podľa typu predaja')}</option>
              {/* §10A.35 step 4 - the registers this franchise has configured, from the endpoint that
                * now lists them, plus whatever this channel already has - §9.650. `SHIPPED_REGISTER_NAMES`
                * is the fallback for a screen drawn before the call answers, and an id nothing names
                * prints itself rather than vanishing. */}
              {registerOptions.map((register) => (
                <option key={register.kasaId} value={register.kasaId}>
                  {register.label}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              <T>Pri tlači sa dá kasa ešte zmeniť.</T>
            </p>
          </Field>

          {/* §9.38 - *farbičkovanie, ako je možné pri kategóriách*, and literally so: the same twelve
            * swatches and the same `categoryColour` gate, so a value this build does not know renders
            * as no colour on both screens rather than as a broken chip on one. */}
          <Field label={tt('Farba')}>
            <div className="flex flex-wrap gap-2" data-channel-colour-picker>
              <button
                type="button"
                data-channel-colour=""
                aria-label={tt('Bez farby')}
                aria-pressed={form.colour === ''}
                onClick={() => onChange({ ...form, colour: '' })}
                className={cn(
                  'w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] font-bold',
                  form.colour === '' ? 'border-primary text-primary' : 'border-border text-muted-foreground',
                )}
              >
                ×
              </button>
              {CATEGORY_PALETTE.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  data-channel-colour={swatch}
                  aria-label={swatch}
                  aria-pressed={form.colour === swatch}
                  onClick={() => onChange({ ...form, colour: swatch })}
                  style={{ backgroundColor: swatch }}
                  className={cn(
                    'w-7 h-7 rounded-full border-2',
                    form.colour === swatch ? 'border-foreground' : 'border-transparent',
                  )}
                />
              ))}
            </div>
          </Field>

          <div className="space-y-2">
            {/* §9.726 - and no checkbox: ticking *Zaplatí sa hneď* draws *Bez dokladu sa zruší* right under it. */}
            {CAPABILITIES.filter((key) => capabilityApplies(key, form)).map((key) => (
              <label
                key={key}
                className="flex items-start gap-2.5 rounded-xl border border-border p-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={(event) => onChange({ ...form, [key]: event.target.checked })}
                  aria-label={capabilityLabel(key)}
                  data-flag={key}
                  className="w-4 h-4 rounded accent-primary mt-0.5 shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{capabilityLabel(key)}</span>
                  <span className="block text-[11px] text-muted-foreground">{capabilityMeaning(key)}</span>
                </span>
              </label>
            ))}
          </div>

          {/* §9.740, owner O110 - *"na prevádzke aj rozváža šofér je aktívne, tak tam pridáme Ďalšie, že v takomto
            * prípade si môžu zvoliť, ktorá kasa to bude tlačit"*. Only on those two flags together, and saving
            * without them clears it (`channelPayload`), so the till never narrows over a box this screen hides. */}
          {choosesItsRegister(form) && (
            <div className="rounded-xl border border-border p-3 space-y-2" data-channel-print-registers>
              <div>
                <p className="text-sm font-semibold">
                  <T>Kasy na výber pri tlači</T>
                </p>
                <p className="text-[11px] text-muted-foreground">
                  <T>
                    Kanál je na prevádzke aj ho rozváža šofér. Zaškrtnite kasy, na ktorých sa jeho doklad môže
                    vytlačiť - pri Zaplatiť a na nástenke rozvozu si potom obsluha vyberie jednu z nich. Jedna
                    zaškrtnutá kasa sa vyberie sama. Bez zaškrtnutia sa tlačí ako doteraz.
                  </T>
                </p>
              </div>
              {printRegisterOptions.map((kasaId) => (
                <label key={kasaId} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.printKasaIds.includes(kasaId)}
                    onChange={(event) =>
                      onChange({
                        ...form,
                        printKasaIds: togglePrintRegister(
                          form.printKasaIds,
                          kasaId,
                          event.target.checked,
                          printRegisterOptions,
                        ),
                      })
                    }
                    aria-label={registerName(kasaId, registerRows, SHIPPED_REGISTER_NAMES)}
                    data-print-register-option={kasaId}
                    className="w-4 h-4 rounded accent-primary"
                  />
                  {registerName(kasaId, registerRows, SHIPPED_REGISTER_NAMES)}
                </label>
              ))}
            </div>
          )}

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => onChange({ ...form, isActive: event.target.checked })}
              aria-label={tt('Zapnutý')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Zapnutý - vypnutý kanál sa nedá vybrať pre novú objednávku</T>
          </label>

          {problems.length > 0 && (
            <div className="space-y-1" data-channel-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    isBlocking(problem) ? 'text-red-500' : 'text-amber-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <ProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            data-save-channel
            onClick={onSave}
            disabled={saving || !canSave(form, others)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function ProblemText({ problem }: { problem: ChannelProblem }) {
  if (problem.kind === 'no-name') return <T>Názov je povinný.</T>
  if (problem.kind === 'no-slug') return <T>Kľúč je povinný.</T>
  if (problem.kind === 'bad-slug') {
    return <T>Kľúč môže mať len malé písmená, číslice a podčiarkovník.</T>
  }
  if (problem.kind === 'duplicate-slug') {
    return (
      <span>
        <T>Tento kľúč už má</T> <strong>{problem.other}</strong> -{' '}
        <T>server by ich nevedel rozlíšiť.</T>
      </span>
    )
  }
  if (problem.kind === 'sort-not-whole') return <T>Poradie je celé číslo.</T>
  if (problem.kind === 'address-without-driver') {
    return <T>Kanál s adresou bez šoféra: objednávka sa dá prepnúť na „na ceste“ bez toho, aby ju niekto viezol.</T>
  }
  return <T>Platba neskôr sa účtuje na stôl - bez „obsluhuje stoly“ sa účet nemá kde otvoriť.</T>
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}
